/**
 * Keyboard shortcuts: a small, user-remappable layer.
 *
 * A binding is a sequence of one or two COMBOS. A combo is a string `[Mod+][Alt+][Shift+]<code>`
 * where `<code>` is a `KeyboardEvent.code` (`KeyC`, `Slash`, `Digit7`, ...): the PHYSICAL key, so
 * the Russian and English layouts share one binding (`KeyC` is `c` and `с`; `Shift+Slash` is `?`
 * in English and `,` in Russian). `Mod` means Ctrl, or Cmd on macOS. As a fallback for layouts
 * where the physical position differs (Dvorak) the typed ASCII character is accepted too.
 *
 * Defaults: `c` new task, `Mod+K` search, `g` then `w` workspace, `?` help. They are never
 * handled while typing (inputs, textareas, selects, contenteditable), during IME composition, or
 * with a dialog open. Escape belongs to dialogs/menus/panels and is not remappable.
 */
import { useEffect, useRef } from 'react';

export type ShortcutAction = 'new-task' | 'search' | 'go-workspace' | 'help' | 'switch-board';
export type ShortcutGroup = 'navigation' | 'tasks' | 'search' | 'general';
/** One or two combos; empty = the action has no shortcut. */
export type Binding = readonly string[];
export type ShortcutBindings = Record<ShortcutAction, Binding>;
/** What the account stores: only actions that differ from the default. */
export type ShortcutOverrides = Partial<Record<ShortcutAction, string[]>>;

export const SHORTCUT_ACTIONS: readonly { id: ShortcutAction; group: ShortcutGroup }[] = [
  { id: 'go-workspace', group: 'navigation' },
  { id: 'new-task', group: 'tasks' },
  { id: 'search', group: 'search' },
  { id: 'help', group: 'general' },
  { id: 'switch-board', group: 'navigation' },
];

export const DEFAULT_BINDINGS: ShortcutBindings = {
  'go-workspace': ['KeyG', 'KeyW'],
  'new-task': ['KeyC'],
  search: ['Mod+KeyK'],
  help: ['Shift+Slash'],
  'switch-board': ['KeyG', 'KeyB'],
};

export const MAX_COMBOS = 2;

export function resolveBindings(overrides: ShortcutOverrides | undefined): ShortcutBindings {
  const result = { ...DEFAULT_BINDINGS };
  if (!overrides) return result;
  for (const { id } of SHORTCUT_ACTIONS) {
    const own = overrides[id];
    if (own) result[id] = own;
  }
  return result;
}

export function sameBinding(a: Binding, b: Binding): boolean {
  return a.length === b.length && a.every((combo, index) => combo === b[index]);
}

/** The overrides to store: bindings equal to their default are left out. */
export function toOverrides(bindings: ShortcutBindings): ShortcutOverrides {
  const overrides: ShortcutOverrides = {};
  for (const { id } of SHORTCUT_ACTIONS) {
    if (!sameBinding(bindings[id], DEFAULT_BINDINGS[id])) overrides[id] = [...bindings[id]];
  }
  return overrides;
}

// ---------------------------------------------------------------------------------------------
// Event -> combos
// ---------------------------------------------------------------------------------------------

/** Typed ASCII character -> the US-layout code that produces it (and whether with Shift). */
const CHAR_TO_CODE: Record<string, { code: string; shift: boolean }> = {
  '?': { code: 'Slash', shift: true },
  '/': { code: 'Slash', shift: false },
  ',': { code: 'Comma', shift: false },
  '.': { code: 'Period', shift: false },
  ';': { code: 'Semicolon', shift: false },
  "'": { code: 'Quote', shift: false },
  '[': { code: 'BracketLeft', shift: false },
  ']': { code: 'BracketRight', shift: false },
  '\\': { code: 'Backslash', shift: false },
  '-': { code: 'Minus', shift: false },
  '=': { code: 'Equal', shift: false },
  '`': { code: 'Backquote', shift: false },
};

const SUPPORTED_CODE =
  /^(Key[A-Z]|Digit[0-9]|F([1-9]|1[0-2])|Slash|Backslash|Comma|Period|Semicolon|Quote|BracketLeft|BracketRight|Minus|Equal|Backquote)$/;

function prefix(event: KeyboardEvent, shift: boolean): string {
  const mod = event.ctrlKey || event.metaKey ? 'Mod+' : '';
  return `${mod}${event.altKey ? 'Alt+' : ''}${shift ? 'Shift+' : ''}`;
}

/** Candidate combos for a key event: the physical key first, then the typed character. */
export function eventCombos(event: KeyboardEvent): string[] {
  const combos: string[] = [];
  if (SUPPORTED_CODE.test(event.code)) combos.push(`${prefix(event, event.shiftKey)}${event.code}`);
  const key = event.key;
  if (key.length === 1) {
    const upper = key.toUpperCase();
    if (/^[A-Z]$/.test(upper)) {
      combos.push(`${prefix(event, event.shiftKey)}Key${upper}`);
    } else if (/^[0-9]$/.test(key)) {
      combos.push(`${prefix(event, event.shiftKey)}Digit${key}`);
    } else {
      const known = CHAR_TO_CODE[key];
      if (known) combos.push(`${prefix(event, known.shift)}${known.code}`);
    }
  }
  return [...new Set(combos)];
}

export type RecordResult =
  | { kind: 'combo'; combo: string }
  | { kind: 'modifier' }
  | { kind: 'cancel' }
  | { kind: 'rejected'; reason: 'unsupported' | 'reserved' };

const RESERVED_LETTERS = 'ACVXZYSPFRWTNQLDHJUO'.split('');
/** Combos the browser or the page itself owns (clipboard, tabs, reload, ...). */
const RESERVED = new Set([
  ...RESERVED_LETTERS.map((letter) => `Mod+Key${letter}`),
  ...RESERVED_LETTERS.map((letter) => `Mod+Shift+Key${letter}`),
  'F5',
  'F11',
  'F12',
]);

/** The combo to RECORD for an event, or why it cannot be bound. */
export function recordFromEvent(event: KeyboardEvent): RecordResult {
  if (event.key === 'Escape') return { kind: 'cancel' };
  if (['Control', 'Shift', 'Alt', 'Meta', 'AltGraph', 'CapsLock'].includes(event.key)) {
    return { kind: 'modifier' };
  }
  const combo = SUPPORTED_CODE.test(event.code)
    ? `${prefix(event, event.shiftKey)}${event.code}`
    : eventCombos(event)[0];
  if (!combo) return { kind: 'rejected', reason: 'unsupported' };
  if (RESERVED.has(combo)) return { kind: 'rejected', reason: 'reserved' };
  return { kind: 'combo', combo };
}

// ---------------------------------------------------------------------------------------------
// Conflicts and display
// ---------------------------------------------------------------------------------------------

/** Two bindings collide when they are equal or one is the first step of the other's chord. */
export function bindingsCollide(a: Binding, b: Binding): boolean {
  if (a.length === 0 || b.length === 0) return false;
  const shorter = a.length <= b.length ? a : b;
  const longer = a.length <= b.length ? b : a;
  return shorter.every((combo, index) => combo === longer[index]);
}

/**
 * The same check the server runs on `PATCH /account` (shared vectors: `docs/shortcut-vectors.json`):
 * the effective bindings (defaults + overrides) must not collide, and no override may be reserved.
 */
export function findProblem(
  overrides: ShortcutOverrides,
): 'SHORTCUT_RESERVED' | 'SHORTCUT_CONFLICT' | null {
  for (const combos of Object.values(overrides)) {
    if (combos.some((combo) => RESERVED.has(combo))) return 'SHORTCUT_RESERVED';
  }
  const bindings = resolveBindings(overrides);
  const ids = SHORTCUT_ACTIONS.map((action) => action.id);
  for (const [index, first] of ids.entries()) {
    for (const second of ids.slice(index + 1)) {
      if (bindingsCollide(bindings[first], bindings[second])) return 'SHORTCUT_CONFLICT';
    }
  }
  return null;
}

/** The first OTHER action whose binding collides with `binding`. */
export function findConflict(
  bindings: ShortcutBindings,
  action: ShortcutAction,
  binding: Binding,
): ShortcutAction | null {
  for (const { id } of SHORTCUT_ACTIONS) {
    if (id !== action && bindingsCollide(bindings[id], binding)) return id;
  }
  return null;
}

const PUNCT: Record<string, { en: string; enShift: string; ru: string; ruShift: string }> = {
  Slash: { en: '/', enShift: '?', ru: '.', ruShift: ',' },
  Comma: { en: ',', enShift: '<', ru: 'Б', ruShift: 'Б' },
  Period: { en: '.', enShift: '>', ru: 'Ю', ruShift: 'Ю' },
  Semicolon: { en: ';', enShift: ':', ru: 'Ж', ruShift: 'Ж' },
  Quote: { en: "'", enShift: '"', ru: 'Э', ruShift: 'Э' },
  BracketLeft: { en: '[', enShift: '{', ru: 'Х', ruShift: 'Х' },
  BracketRight: { en: ']', enShift: '}', ru: 'Ъ', ruShift: 'Ъ' },
  Backslash: { en: '\\', enShift: '|', ru: '\\', ruShift: '/' },
  Minus: { en: '-', enShift: '_', ru: '-', ruShift: '_' },
  Equal: { en: '=', enShift: '+', ru: '=', ruShift: '+' },
  Backquote: { en: '`', enShift: '~', ru: 'Ё', ruShift: 'Ё' },
};

export interface ComboLabel {
  /** Modifier names, e.g. `["Ctrl"]` or `["⌘"]`. */
  modifiers: string[];
  /** The key as typed on the English layout. */
  key: string;
  /** The same physical key on the Russian layout, when it differs. */
  ruKey: string | null;
}

export function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
}

/** Display parts of a stored combo (platform aware). `Shift+Slash` shows as `?`. */
export function describeCombo(combo: string, mac = isMac()): ComboLabel {
  const parts = combo.split('+');
  const code = parts[parts.length - 1] ?? '';
  const flags = new Set(parts.slice(0, -1));
  const modifiers: string[] = [];
  if (flags.has('Mod')) modifiers.push(mac ? '⌘' : 'Ctrl');
  if (flags.has('Alt')) modifiers.push(mac ? '⌥' : 'Alt');
  const shift = flags.has('Shift');
  const punct = PUNCT[code];
  let key: string;
  let ruKey: string | null = null;
  if (punct) {
    key = shift ? punct.enShift : punct.en;
    const ru = shift ? punct.ruShift : punct.ru;
    ruKey = ru !== key ? ru : null;
  } else {
    if (shift) modifiers.push(mac ? '⇧' : 'Shift');
    if (code.startsWith('Key')) {
      key = code.slice(3);
      // Letters: the Cyrillic twin looks like the Latin key; punctuation is where layouts differ.
    } else if (code.startsWith('Digit')) key = code.slice(5);
    else key = code;
  }
  return { modifiers, key, ruKey };
}

/** One-line text for tooltips: `Ctrl+K`, `C`, `G then W`. */
export function bindingText(binding: Binding, mac = isMac()): string {
  return binding
    .map((combo) => {
      const label = describeCombo(combo, mac);
      return [...label.modifiers, label.key].join('+');
    })
    .join(' → ');
}

// ---------------------------------------------------------------------------------------------
// Runtime
// ---------------------------------------------------------------------------------------------

type Handler = () => void;
const handlers = new Map<ShortcutAction, Handler>();

/** A page registers what an action means there; the handler is removed on unmount. */
export function useShortcutHandler(action: ShortcutAction, handler: Handler | null): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  const active = handler !== null;
  useEffect(() => {
    if (!active) return;
    const run = () => {
      latest.current?.();
    };
    handlers.set(action, run);
    return () => {
      if (handlers.get(action) === run) handlers.delete(action);
    };
  }, [action, active]);
}

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.closest('[contenteditable=""], [contenteditable="true"]') !== null ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/** A single-line text field (not a textarea / editor): Ctrl/Cmd shortcuts may fire from it. */
function isPlainInput(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement &&
    ['text', 'search', 'email', 'url', 'tel', 'number', ''].includes(target.type) &&
    target.closest('[data-no-shortcuts]') === null
  );
}

const CHORD_MS = 1200;
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'AltGraph', 'CapsLock']);

/** Mounted once (the app shell). The single listener always reads the latest bindings. */
export function useShortcutListener(bindings: ShortcutBindings, enabled = true): void {
  const latest = useRef(bindings);
  const switchedOn = useRef(enabled);
  useEffect(() => {
    latest.current = bindings;
    switchedOn.current = enabled;
  });
  useEffect(() => {
    let pending: { combos: string[]; at: number } | null = null;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.key === 'Process') return;
      if (!switchedOn.current) return; // the master switch: bindings stay stored, nothing runs
      if (event.repeat || MODIFIER_KEYS.has(event.key)) return;
      if (document.querySelector('dialog[open]')) return;
      // While typing only a Ctrl/Cmd combination works, and only in an ordinary <input> (a
      // search or name field): textareas / Markdown editors / contenteditable keep their keys.
      const typing = isTyping(event.target);
      if (typing && !isPlainInput(event.target)) return;
      const combos = eventCombos(event);
      const live = pending && Date.now() - pending.at < CHORD_MS ? pending : null;
      pending = null;
      if (combos.length === 0) return;
      const entries = SHORTCUT_ACTIONS.map(({ id }) => [id, latest.current[id]] as const);
      let action: ShortcutAction | null = null;
      if (live) {
        action =
          entries.find(
            ([, binding]) =>
              binding.length === 2 &&
              live.combos.includes(binding[0] ?? '') &&
              combos.includes(binding[1] ?? ''),
          )?.[0] ?? null;
      }
      action ??=
        entries.find(
          ([, binding]) => binding.length === 1 && combos.includes(binding[0] ?? ''),
        )?.[0] ?? null;
      if (typing && action && !(latest.current[action][0] ?? '').startsWith('Mod+')) return;
      if (typing && !action) return;
      if (!action) {
        if (entries.some(([, b]) => b.length === 2 && combos.includes(b[0] ?? ''))) {
          pending = { combos, at: Date.now() };
        }
        return;
      }
      const handler = handlers.get(action);
      if (handler) {
        event.preventDefault();
        handler();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
}
